import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Image } from "antd";
import type React from "react";
import { useState } from "react";

interface EventImageCarouselProps {
	images: Array<{ picUrl: string; showOrder?: number }>;
	title?: string;
	imageBaseUrl?: string;
	primaryColor?: string;
}

export const EventImageCarousel: React.FC<EventImageCarouselProps> = ({
	images,
	title = "",
	imageBaseUrl = "",
	primaryColor = "#99CA29",
}) => {
	const [currentIndex, setCurrentIndex] = useState(0);

	const sortedImages = [...images].sort(
		(a, b) => (a.showOrder || 0) - (b.showOrder || 0),
	);

	if (sortedImages.length === 0) {
		return (
			<div
				style={{
					width: "100%",
					height: "350px",
					backgroundColor: "#f3f4f6",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					borderRadius: "8px",
				}}
			>
				<Image
					preview={false}
					src="/no-image.png"
					alt={title}
					style={{ maxHeight: "350px", objectFit: "cover" }}
					fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3PTWBSGcbGzM6GCKqlIBRV0dHRJFarQ0eUT8LH4BnRU0NHR0UEFVdIlFRV7TzRksomPY8uykTk/zewQfKw/9teleZTaUY3x7P9XFXJ/Pw=="
				/>
			</div>
		);
	}

	const handlePrev = () => {
		setCurrentIndex((prev) =>
			prev === 0 ? sortedImages.length - 1 : prev - 1,
		);
	};

	const handleNext = () => {
		setCurrentIndex((prev) =>
			prev === sortedImages.length - 1 ? 0 : prev + 1,
		);
	};

	const getImageUrl = (picUrl: string) => {
		if (picUrl.startsWith("http") || picUrl.startsWith("/")) {
			return picUrl;
		}
		return `${imageBaseUrl}${picUrl}`;
	};

	return (
		<div>
			<div style={{ position: "relative" }}>
				<div
					style={{
						width: "100%",
						height: "350px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						overflow: "hidden",
						borderRadius: "8px",
						backgroundColor: "#f3f4f6",
					}}
				>
					<Image
						src={getImageUrl(sortedImages[currentIndex]?.picUrl)}
						alt={title}
						style={{
							maxWidth: "100%",
							maxHeight: "350px",
							objectFit: "contain",
						}}
						fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3PTWBSGcbGzM6GCKqlIBRV0dHRJFarQ0eUT8LH4BnRU0NHR0UEFVdIlFRV7TzRksomPY8uykTk/zewQfKw/9teleZTaUY3x7P9XFXJ/Pw=="
					/>
				</div>
				{sortedImages.length > 1 && (
					<>
						<button
							type="button"
							onClick={handlePrev}
							style={{
								position: "absolute",
								left: "8px",
								top: "50%",
								transform: "translateY(-50%)",
								width: "40px",
								height: "40px",
								borderRadius: "50%",
								backgroundColor: "rgba(255, 255, 255, 0.8)",
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								border: `1px solid ${primaryColor}`,
							}}
						>
							<LeftOutlined style={{ color: primaryColor }} />
						</button>
						<button
							type="button"
							onClick={handleNext}
							style={{
								position: "absolute",
								right: "8px",
								top: "50%",
								transform: "translateY(-50%)",
								width: "40px",
								height: "40px",
								borderRadius: "50%",
								backgroundColor: "rgba(255, 255, 255, 0.8)",
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								border: `1px solid ${primaryColor}`,
							}}
						>
							<RightOutlined style={{ color: primaryColor }} />
						</button>
					</>
				)}
			</div>
			{sortedImages.length > 1 && (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						gap: "8px",
						marginTop: "8px",
						flexWrap: "wrap",
					}}
				>
					{sortedImages.map((image, index) => (
						<button
							type="button"
							key={image.picUrl}
							onClick={() => setCurrentIndex(index)}
							style={{
								padding: "2px",
								borderRadius: "4px",
								cursor: "pointer",
								backgroundColor: "white",
								opacity: currentIndex === index ? 0.7 : 1,
								border:
									currentIndex === index
										? `2px solid ${primaryColor}`
										: "1px solid #d9d9d9",
							}}
						>
							<img
								src={getImageUrl(image.picUrl)}
								alt={`${title} ${index + 1}`}
								style={{
									width: "80px",
									height: "50px",
									objectFit: "cover",
									borderRadius: "2px",
								}}
							/>
						</button>
					))}
				</div>
			)}
		</div>
	);
};
